import { useMemo, type ReactNode } from "react";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import { TrackingScope } from "@b2bportal/core-tracking";
import {
  CoreHotelsComponent,
  type MapCoordinates,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { Lodging } from "@b2bportal/lodging-api";
import { useHotelAvailabilityMap } from "@tenant/hotels";
import { type GestureHandlingType, Map, SkeletonLoading } from "@tenant/ui";
import clsx from "clsx";
import { useHotelSlot } from "../../../../slots/create";
import defaultStyles from "./HotelAvailabilityMap.module.scss";

type BaseHotelAvailabilityMapProps = {
  centroid?: MapCoordinates;
  hotels: Lodging[];
  hoveredId?: string;
  zoom?: number;
  loading?: boolean;
  mapChildren?: ReactNode | undefined;
  gestureHandling?: GestureHandlingType;
} & PlatformComponentProps;

type ClusteredMapProps = BaseHotelAvailabilityMapProps & {
  useClusters?: true;
  markerLimit?: undefined;
};

type NonClusteredMapProps = BaseHotelAvailabilityMapProps & {
  useClusters: false;
  markerLimit?: number;
};

export type HotelAvailabilityMapProps =
  | ClusteredMapProps
  | NonClusteredMapProps;

const COMPONENT_KEY = CoreHotelsComponent.HotelAvailabilityMap;

export const HotelAvailabilityMap = (props: HotelAvailabilityMapProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { children, mapChildren, className } = props;
  const slots = useHotelSlot();

  const { context, handlers, text } = useHotelAvailabilityMap(props);

  const HotelMapRedoSearchButton =
    slots[CoreHotelsComponent.HotelMapRedoSearchButton];
  const HotelMapMarkers = slots[CoreHotelsComponent.HotelMapMarkers];

  const searchButtonAttributes = useMemo(
    () => ({
      pageIndex: 0,
      pagePropertyCount: props.hotels.length,
    }),
    [props.hotels]
  );

  if (context.centroid == null || props.loading === true) {
    return <SkeletonLoading className={clsx(cn("loading"), className)} />;
  }

  return (
    <div className={clsx(block, className)}>
      <Map
        centroid={context.centroid}
        searchBoundingBox={context.searchBoundingBox}
        zoom={context.zoom}
        points={context.points}
        MapMarker={HotelMapMarkers}
        highlightedId={context.hoveredId}
        className={cn("map")}
        onMapChange={handlers.handleMapChange}
        gestureHandling={context.gestureHandling}
        {...(context.useClusters === false
          ? { useClusters: false, markerLimit: context.markerLimit }
          : { useClusters: true })}
      >
        {mapChildren}
      </Map>

      {handlers.handleMapSearch != null && HotelMapRedoSearchButton != null ? (
        <TrackingScope trackingAttributes={searchButtonAttributes}>
          <HotelMapRedoSearchButton
            className={cn("search")}
            onClick={handlers.handleMapSearch}
            label={text.searchMap}
          />
        </TrackingScope>
      ) : null}
      {children}
    </div>
  );
};
